import { Dispatch, RefObject, SetStateAction, useState } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import useMediaSelectContext from './useMediaSelectContext'

const joinText = (...text: Array<string>) => text.join(' ')

type Props = {
  setIsControlsHidden: Dispatch<SetStateAction<boolean>>
  containerRef: RefObject<HTMLDivElement | null>
  photosCount: number
  onSortEnd: (oldIndex: number, newIndex: number) => void
}

const useMediaSelectSort = ({
  setIsControlsHidden,
  containerRef,
  photosCount,
  onSortEnd,
}: Props) => {
  const translate = useTranslate('item_upload.media_select')
  const { changePhotoOrder } = useMediaSelectContext()

  const [ariaText, setAriaText] = useState('')

  const handleSortStart = (index: number) => {
    setAriaText(
      joinText(
        translate('a11y.uploaded_image_grabbed', { count: index + 1 }),
        translate('a11y.current_order_position', { position: index + 1, total: photosCount }),
        translate('a11y.active_reorder'),
      ),
    )

    setIsControlsHidden(true)
  }

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    containerRef.current?.focus()
    setIsControlsHidden(false)

    if (oldIndex === newIndex) {
      setAriaText(
        joinText(
          translate('a11y.uploaded_image_cancelled', { count: oldIndex + 1 }),
          translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
          translate('a11y.inactive_reorder'),
        ),
      )

      return
    }

    setAriaText(
      joinText(
        translate('a11y.uploaded_image_saved', { count: oldIndex + 1 }),
        translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
        translate('a11y.inactive_reorder'),
      ),
    )

    changePhotoOrder({ oldIndex, newIndex })
    onSortEnd(oldIndex, newIndex)
  }

  const handleSortOver = (oldIndex: number, newIndex: number) => {
    setAriaText(
      joinText(
        translate('a11y.uploaded_image_moved', { count: oldIndex + 1 }),
        translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
        translate('a11y.active_reorder'),
      ),
    )
  }

  return {
    ariaText,
    handleSortStart,
    handleSortEnd,
    handleSortOver,
  }
}

export default useMediaSelectSort
